/* eslint-disable no-use-before-define */

/* eslint-disable fp/no-mutating-methods */
import {
  createAsyncThunk,
  createSlice as _createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { sortBy, prop } from 'ramda'
import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { UserExtra as User, UserInvite } from 'src/user/user.types'
import {
  Assignment,
  Class,
  Course,
  Credential,
  EMSID,
  ExternalCertificate,
  LearningPath,
  License,
  LinkedService,
  LiveCertificate,
  NREMTLicense,
  RecordedCertificate,
  ScheduledAssignment,
  Section,
  TitleScreen,
  TrainingSeries,
  VerificationStatus,
} from 'src/v3/core/types'
import type {
  CourseV2Accreditation,
  CourseV2Content,
  CourseV2ContentWire,
} from 'src/v3/classroom-v2/types'
import { pLimitFiveService } from 'src/v3/utilities/pLimitService'
import { CertificatesRollupData } from '../profile/v4/certificates/types'
import type {
  RosterCredentialItem,
  RosterUserActivityResponse,
  SelectedUser,
} from './roster/types'
import type {
  CreateScheduledAssignmentPayload,
  UpdateScheduledAssignmentPayload,
} from './scheduled-assignments/types'
import type {
  AnalyticsRow,
  AssignmentAnalyticsMetrics,
  AssignmentAnalyticsFilters,
} from './assignment-analytics/types'
import type { GroupsV2Row, GroupsV2Filters } from './groups-v2/types'

export type PaginatedResponse<T> = {
  data: T[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export let fetchAllPages = async <T>(
  endpoint: string,
  pageSize: number = 1500,
  additionalParams: Record<string, any> = {},
): Promise<T[]> => {
  let firstResponse = await axios.get<PaginatedResponse<T>>(endpoint, {
    params: { page: 1, pageSize, ...additionalParams },
  })

  let allData = [...firstResponse.data.data]
  let { totalPages } = firstResponse.data.pagination

  if (totalPages > 1) {
    let remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)

    let remainingRequests = remainingPages.map((page) =>
      pLimitFiveService(() =>
        axios.get<PaginatedResponse<T>>(endpoint, {
          params: { page, pageSize, ...additionalParams },
        }),
      ),
    )

    let remainingResponses = await Promise.all(remainingRequests)
    let remainingData = remainingResponses.flatMap((res) => res.data.data)
    allData = [...allData, ...remainingData]
  }

  return allData
}

export type CompletedAssignment = {
  assignmentID: string
  dateCompleted: string
  linkedServiceID: string
  userID: string
}

export type UserGroup = {
  id?: string
  name: string
  linkedServiceID: string
  users?: string[]
  createdTime?: string
  updatedTime?: string
}

export type UserGroupFormValues = {
  id?: string
  name: string
  linkedServiceID: string
}

export type UserHours = {
  courseCode: string
  courseLength: number
  courseName: string
  firstName: string
  lastName: string
  createdTime: number
}

export type TimeUnit = 'week' | 'month' | 'year' | 'allTime'

export type UserHoursV3 = {
  linkedServiceID: string
} & Record<
  TimeUnit,
  {
    userID: string
    hours: number
  }[]
>

export type CompletedCourse = {
  assignmentID: string
  courseID: string
  dateCompleted: string
  userID: string
}

type QuizByPassFail = {
  className: string
  courseCode: string
  quizId: string
  totalAttempts: number
  passedCount: number
  failedCount: number
  passedRate: number
  failedRate: number
}

export type QuestionByPassFail = {
  className: string
  quizName: string
  questionText: string
  courseCode: string
  quizId: string
  totalAttempts: number
  passedCount: number
  failedCount: number
  passedRate: number
  failedRate: number
}

export type QuestionBreakdown = {
  quizID: string
  questionID: string
  questionText: string
  choiceText: string
  numberTimesPicked: number
  percentChosen: number
  isCorrect: string
}

export type QuizData = {
  quizRate: QuizByPassFail[]
  questionRate: QuestionByPassFail[]
  questionBreakdown: QuestionBreakdown[]
}

// V4 API Types - optimized for efficient data fetching
export type AssignmentSummaryV3 = {
  id: string
  name: string
  linkedServiceID: string
  linkedServiceName: string
  createdTime: string | null
  dueDate: string | null
  courseCount: number
  assignedUserCount: number
  completedUserCount: number
  completionPercentage: number
  lastActivityDate: string | null
}

// Placeholder types for V4 - these may need to be defined based on actual API responses
export type CourseWithStats = {
  id: string
  name: string
  displayName?: string
  completionCount: number
  totalAssigned: number
  averageScore?: number
}

type AssignmentStatus = 'NotStarted' | 'InProgress' | 'Completed'

export type UserAssignmentStatus = {
  id: string
  status: AssignmentStatus
  completedCourses: string[]
  totalCourses: number
  lastActivity?: string
}

export type AssignmentStats = {
  totalUsers: number
  completedUsers: number
  inProgressUsers: number
  notStartedUsers: number
  averageCompletionTime?: number
  completionRate: number
}

export type CourseProgressDetail = {
  courseID: string
  courseName: string
  status: AssignmentStatus
  progress?: number
  completionDate?: string | null
  score?: number
}

export type AssignmentDetailV3 = {
  id: string
  name: string
  linkedServiceID: string
  createdTime: string
  dueDate: string | null
  courses: CourseWithStats[]
  users: UserAssignmentStatus[]
  statistics: AssignmentStats
}

export type UserAssignmentProgress = {
  userID: string
  userName: string
  email: string
  status: AssignmentStatus
  completedCourses: number
  totalCourses: number
  progressPercentage: number
  lastActivityDate: string | null
  completionDate: string | null
  courseProgress?: CourseProgressDetail[]
}

export type SingleUserCourseWithStats = {
  id: string
  name: string
  displayName: string
  length: number
  courseCompletionStatus: 'Completed' | null
}

export type SingleUserAssignmentDetails = {
  id: string
  name: string
  createdTime: string
  dueDate: string
  totalLength: number
  assignmentCompletionPercentage: number
  courses: SingleUserCourseWithStats[]
}

export type CourseUserProgress = {
  userID: string
  userName: string
  email: string
  courseStatus: Exclude<AssignmentStatus, 'InProgress'>
  courseCompletionDate: string | null
  assignmentStatus: AssignmentStatus
  assignmentProgress: number
}

export type AssignmentCreditSource = 'ManagerCredit' | 'ThresholdCredit'

export type AssignmentCredit = {
  id: string
  assignmentID: string
  userID: string
  source: AssignmentCreditSource
  grantedByUserID: string | null
  createdTime: string | null
}

export type AssignmentCourseCredit = {
  id: string
  assignmentID: string
  assignmentCourseID: string
  courseID: string
  userID: string
  source: AssignmentCreditSource
  grantedByUserID: string | null
  createdTime: string | null
}

export type AssignmentCreditsResponse = {
  assignmentCredits: AssignmentCredit[]
  assignmentCourseCredits: AssignmentCourseCredit[]
}

export type LightweightUser = {
  id: string
  primaryEmail: string
  firstName: string
  lastName: string
  licenseTypes: string[]
}

export type ElevatedUsersResponse = {
  creators: string[]
  managers: string[]
}

export type VerificationStatusesResponse = {
  id: string
  name: string
  number: string
  type: string
  state?: string
  expiration: string
  status: VerificationStatus
  imageName?: string
}

export let fetchSections = createAsyncThunk('manage/fetchSectionsStatus', () =>
  fetchAllPages<Section>('/api/v2/manage/sections'),
)

export let fetchUserHours = createAsyncThunk(
  'manage/fetchUserHoursStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<UserHoursV3[]>('/api/v2/manage/dashboard/user-hours')
        .then(pluckData),
    ),
)

export let fetchQuizAnalytics = createAsyncThunk(
  'manage/fetchQuizAnalyticsStatus',
  (args: { linkedServiceID: string; startDate: string; endDate: string }) =>
    pLimitFiveService(() => {
      let searchParams = new URLSearchParams()
      searchParams.append('linkedServiceId', args.linkedServiceID)
      searchParams.append('startDate', args.startDate)
      searchParams.append('endDate', args.endDate)

      return axios
        .get<QuizData>(
          `/api/quiz-events-by-linked-service?${searchParams.toString()}`,
        )
        .then(pluckData)
    }),
)

export let fetchGroups = createAsyncThunk('manage/fetchGroupsStatus', () =>
  fetchAllPages<UserGroup>('/api/v2/manage/groups'),
)

export let fetchUsers = createAsyncThunk('manage/fetchUsersStatus', () =>
  fetchAllPages<User>('/api/v2/manage/users'),
)

export let fetchUserEMSID = createAsyncThunk(
  'manage/fetchUserEMSIDStatus',
  (userID: string) =>
    axios.get<EMSID>(`/api/manage/ems-id/${userID}`).then(pluckData),
)

export let fetchSelectedUser = createAsyncThunk(
  'manage/fetchSelectedUserStatus',
  (id: string) =>
    axios.get<SelectedUser>(`/api/v3/manage/users/${id}`).then(pluckData),
)

export let fetchSelectedUserCertificates = createAsyncThunk(
  'manage/fetchSelectedUserCertificatesStatus',
  ({ id, linkedServiceID }: { id: string; linkedServiceID: string }) =>
    axios
      .get<CertificatesRollupData>(
        `/api/v3/manage/${linkedServiceID}/users/${id}/certificates`,
      )
      .then(pluckData),
)

export let fetchSelectedUserLicensesCredentials = createAsyncThunk(
  'manage/fetchSelectedUserLicensesCredentialsStatus',
  (id: string) =>
    axios
      .get<RosterCredentialItem[]>(
        `/api/v3/manage/users/${id}/licenses-credentials`,
      )
      .then(pluckData),
)

export let fetchSelectedUserActivity = createAsyncThunk(
  'manage/fetchSelectedUserActivityStatus',
  ({
    userID,
    linkedServiceID,
    startDate,
    endDate,
  }: {
    userID: string
    linkedServiceID: string
    startDate?: string
    endDate?: string
  }) =>
    pLimitFiveService(() =>
      axios
        .get<RosterUserActivityResponse>(
          `/api/v3/manage/${linkedServiceID}/users/${userID}/activity`,
          {
            params: {
              startDate,
              endDate,
              limit: 100,
            },
          },
        )
        .then(pluckData),
    ),
)

export let fetchAssignments = createAsyncThunk(
  'manage/fetchAssignmentsStatus',
  () => fetchAllPages<Assignment>('/api/v2/manage/assignments'),
)

export let fetchAssignmentsV4 = createAsyncThunk(
  'manage/fetchAssignmentsV4Status',
  () => fetchAllPages<Assignment>('/api/v3/manage/assignments'),
)

export let fetchCompletedAssignmentsWithinThreshold = createAsyncThunk(
  'manage/fetchCompletedAssignmentsWithinThresholdStatus',
  (id: string) =>
    pLimitFiveService(() =>
      axios
        .get<CompletedCourse[]>(
          `/api/manage/assignment-completed-within-threshold?assignmentID=${id}`,
        )
        .then(pluckData),
    ),
)

export let fetchCompletedAssignments = createAsyncThunk(
  'manage/fetchCompletedAssignmentsStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<CompletedAssignment[]>('/api/v3/manage/completed-assignments')
        .then(pluckData),
    ),
)

export let fetchCompletedCourses = createAsyncThunk(
  'manage/fetchCompletedCoursesStatus',
  () =>
    fetchAllPages<CompletedCourse>('/api/manage/assignment-courses/completed'),
)

export let fetchTrainingSeries = createAsyncThunk(
  'manage/fetchTrainingSeriesStatus',
  () => fetchAllPages<TrainingSeries>('/api/v2/manage/training-series'),
)

export let fetchLearningPaths = createAsyncThunk(
  'manage/fetchLearningPathsStatus',
  (linkedServiceID: string) =>
    pLimitFiveService(() =>
      axios
        .get<LearningPath[]>(`/api/manage/${linkedServiceID}/learning-paths`)
        .then(pluckData),
    ),
)

export let fetchFoamfratLearningPaths = createAsyncThunk(
  'manage/fetchFoamfratLearningPathsStatus',
  () =>
    pLimitFiveService(() =>
      axios
        .get<LearningPath[]>('/api/manage/foamfrat/learning-paths')
        .then(pluckData),
    ),
)

export let fetchClasses = createAsyncThunk('manage/fetchClassesStatus', () =>
  fetchAllPages<Class>('/api/v2/manage/classes'),
)

export let fetchCourses = createAsyncThunk('manage/fetchCoursesStatus', () =>
  fetchAllPages<Course>('/api/v2/manage/hydrated-courses'),
)

export let fetchInvites = createAsyncThunk(
  'manage/fetchInvitesStatus',
  (id: string) =>
    pLimitFiveService(() =>
      axios
        .get<UserInvite[]>(
          `/api/v2/manage/linked-service/invites/by-linked-service?linkedServiceID=${id}`,
        )
        .then(pluckData),
    ),
)

export let fetchCertificates = createAsyncThunk(
  'manage/fetchCertificatesStatus',
  () => fetchAllPages<RecordedCertificate>('/api/v2/manage/certificates'),
)

export let fetchLiveCertificates = createAsyncThunk(
  'manage/fetchLiveCertificatesStatus',
  () =>
    fetchAllPages<LiveCertificate>('/api/v2/manage/live-class-certificates'),
)

export let fetchExternalCertificates = createAsyncThunk(
  'manage/fetchExternalCertificatesStatus',
  () =>
    fetchAllPages<ExternalCertificate>('/api/v2/manage/external-certificates'),
)

export let fetchCredentials = createAsyncThunk(
  'manage/fetchCredentialsStatus',
  () => fetchAllPages<Credential>('/api/v2/manage/credentials'),
)

export let fetchLinkedServices = createAsyncThunk(
  'linkedService/fetchLinkedServices',
  () => fetchAllPages<LinkedService>('/api/v2/manage/linked-services'),
)

export let fetchLicenses = createAsyncThunk(
  'manage/fetchLicenses',
  (linkedServiceID: string) =>
    pLimitFiveService(() =>
      axios
        .get<License[]>(
          `/api/manage/licenses?linkedServiceID=${linkedServiceID}`,
        )
        .then(pluckData),
    ),
)

// V4 Assignments Thunks
export let fetchAssignmentSummariesV4 = createAsyncThunk(
  'manage/fetchAssignmentSummariesV4Status',
  () => fetchAllPages<AssignmentSummaryV3>('/api/v3/manage/assignments'),
)

// Assignment Analytics Thunks
export let fetchAssignmentAnalytics = createAsyncThunk(
  'manage/fetchAssignmentAnalyticsStatus',
  async ({
    linkedServiceID,
    startDate,
    endDate,
  }: {
    linkedServiceID: string
    startDate?: string
    endDate?: string
  }): Promise<AnalyticsRow[]> => {
    let additionalParams: Record<string, string> = {}
    if (startDate) additionalParams.startDate = startDate
    if (endDate) additionalParams.endDate = endDate

    return fetchAllPages<AnalyticsRow>(
      `/api/manage/${linkedServiceID}/assignment-analytics`,
      1500,
      additionalParams,
    )
  },
)

export let fetchAssignmentAnalyticsMetrics = createAsyncThunk(
  'manage/fetchAssignmentAnalyticsMetricsStatus',
  async ({
    linkedServiceID,
    startDate,
    endDate,
  }: {
    linkedServiceID: string
    startDate?: string
    endDate?: string
  }): Promise<AssignmentAnalyticsMetrics> => {
    let params: Record<string, string> = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate

    let response = await axios.get<AssignmentAnalyticsMetrics>(
      `/api/manage/${linkedServiceID}/assignment-analytics/metrics`,
      { params },
    )
    return response.data
  },
)

// Groups V2
export let fetchGroupsV2 = createAsyncThunk(
  'manage/fetchGroupsV2Status',
  (linkedServiceID: string): Promise<GroupsV2Row[]> =>
    fetchAllPages<GroupsV2Row>(
      `/api/manage/linked-service/${linkedServiceID}/groups`,
    ),
)

export let fetchAssignmentDetailV4 = createAsyncThunk(
  'manage/fetchAssignmentDetailV4Status',
  (assignmentId: string) =>
    axios
      .get<AssignmentDetailV3>(`/api/v3/manage/assignment/${assignmentId}`)
      .then(pluckData),
)

export let fetchSelectedUserAssignmentDetail = createAsyncThunk(
  'manage/fetchSelectedUserAssignmentDetail',
  ({ userID, assignmentID }: { userID: string; assignmentID: string }) =>
    axios
      .get<SingleUserAssignmentDetails>(
        `/api/v3/manage/users/${userID}/assignments/${assignmentID}`,
      )
      .then(pluckData),
)

export let fetchAssignmentUserProgressV4 = createAsyncThunk(
  'manage/fetchAssignmentUserProgressV4Status',
  ({
    assignmentId,
    page = 1,
    pageSize = 50,
    includeDetails = false,
  }: {
    assignmentId: string
    page?: number
    pageSize?: number
    includeDetails?: boolean
  }) =>
    axios
      .get<PaginatedResponse<UserAssignmentProgress>>(
        `/api/v3/manage/assignment/${assignmentId}/users`,
        {
          params: { page, pageSize, includeDetails },
        },
      )
      .then(pluckData),
)

export let fetchCourseUserProgressV4 = createAsyncThunk(
  'manage/fetchCourseUserProgressV4Status',
  ({
    assignmentId,
    courseId,
    page = 1,
    pageSize = 50,
    status,
  }: {
    assignmentId: string
    courseId: string
    page?: number
    pageSize?: number
    status?: 'completed' | 'not-started'
  }) =>
    axios
      .get<PaginatedResponse<CourseUserProgress>>(
        `/api/v3/manage/assignment/${assignmentId}/course/${courseId}/users`,
        {
          params: { page, pageSize, status },
        },
      )
      .then(pluckData),
)

// New async thunks that fetch ALL pages of data
export let fetchAllAssignmentUserProgressV4 = createAsyncThunk(
  'manage/fetchAllAssignmentUserProgressV4Status',
  ({
    assignmentId,
    includeDetails = false,
  }: {
    assignmentId: string
    includeDetails?: boolean
  }) =>
    fetchAllPages<UserAssignmentProgress>(
      `/api/v3/manage/assignment/${assignmentId}/users`,
      500,
      { includeDetails },
    ),
)

export let fetchAllCourseUserProgressV4 = createAsyncThunk(
  'manage/fetchAllCourseUserProgressV4Status',
  ({
    assignmentId,
    courseId,
    status,
  }: {
    assignmentId: string
    courseId: string
    status?: 'completed' | 'not-started'
  }) =>
    fetchAllPages<CourseUserProgress>(
      `/api/v3/manage/assignment/${assignmentId}/course/${courseId}/users`,
      500,
      { status },
    ),
)

export let fetchAssignmentCredits = createAsyncThunk(
  'manage/fetchAssignmentCredits',
  (assignmentID: string) =>
    axios
      .get<AssignmentCreditsResponse>(
        `/api/manage/assignment/${assignmentID}/credits`,
      )
      .then(pluckData),
)

export let fetchLightweightUsersV4 = createAsyncThunk(
  'manage/fetchLightweightUsersV4Status',
  (linkedServiceID: string) =>
    fetchAllPages<LightweightUser>('/api/v3/manage/users/lightweight', 500, {
      linkedServiceID,
    }),
)

export let fetchElevatedUsers = createAsyncThunk(
  'manage/fetchElevatedUsersStatus',
  (linkedServiceID: string) =>
    axios
      .get<ElevatedUsersResponse>(
        `/api/v3/manage/users/elevated/${linkedServiceID}`,
      )
      .then(pluckData),
)

export let fetchUserDetailsV4 = createAsyncThunk(
  'manage/fetchUserDetailsV4Status',
  async (userIds: string[], { getState }) => {
    let state = getState() as AppRootState
    let existingUsers = state.manage.userDetailsV4
    let missingUserIds = userIds.filter((id) => !existingUsers[id])

    if (missingUserIds.length === 0) {
      return {}
    }

    let response = await axios
      .post<{
        data: User[]
      }>('/api/v3/manage/users/details', { userIds: missingUserIds })
      .then(pluckData)

    let users = response.data || []

    return users.reduce(
      (acc, user) => {
        acc[user.id] = user
        return acc
      },
      {} as Record<string, User>,
    )
  },
)

export let fetchCourseTitleScreen = createAsyncThunk(
  'manage/fetchCourseTitleScreenStatus',
  (courseID: string) =>
    axios
      .get<TitleScreen>(`/api/v2/manage/courses/${courseID}/title-screen`)
      .then(pluckData),
)

type CourseVersionResponse = {
  version: number
  publishedVersion?: number
}

export let fetchManageCourseVersion = createAsyncThunk(
  'manage/fetchManageCourseVersion',
  (courseID: string) =>
    axios
      .get<CourseVersionResponse>(
        `/api/v4/classroom/course/${encodeURIComponent(courseID)}/version`,
      )
      .then(pluckData),
)

export let fetchManageCourseV2Content = createAsyncThunk(
  'manage/fetchManageCourseV2Content',
  (courseID: string) =>
    axios
      .get<CourseV2ContentWire>(
        `/api/classroom/v2/course/${encodeURIComponent(courseID)}/content`,
      )
      .then(pluckData)
      .then((wire): CourseV2Content => ({
        ...wire,
        steps: [],
        accreditation: wire.accreditation.map((a) => {
          let [kind, fields] = Object.entries(a)[0]
          return { kind, ...(fields as object) } as CourseV2Accreditation
        }),
      })),
)

export let exportCredentials = createAsyncThunk(
  'manage/exportCredentials',
  async (linkedServiceID: string) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.get('/manage/credentials/export', {
        params: { linkedServiceID, page, pageSize: 2000 },
        responseType: 'text',
      })

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'credentials-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportCertificates = createAsyncThunk(
  'manage/exportCertificates',
  async (linkedServiceID: string) => {
    let csvChunks = await fetchAllPages<string[]>(
      `/api/v2/manage/certificates/export?linkedServiceID=${linkedServiceID}`,
    )

    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'certificates-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportUserTranscripts = createAsyncThunk(
  'manage/exportUserTranscripts',
  async (params: { userIDs: string[]; startDate: any; endDate: any }) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.post(
        '/api/manage/user/transcripts',
        {
          ...params,
          page,
          pageSize: 2000,
        },
        { responseType: 'text' },
      )

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'user-transcripts-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportLinkedServiceRoster = createAsyncThunk(
  'manage/exportLinkedServiceRoster',
  async (linkedServiceID: string) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.get(
        '/api/v2/manage/linked-service/roster/export',
        {
          params: { linkedServiceID, page, pageSize: 2000 },
          responseType: 'text',
        },
      )

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'roster-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let fetchNREMTLicense = createAsyncThunk(
  'manage/fetchNREMTLicenseStatus',
  (userID: string) =>
    axios.get<NREMTLicense>(`/api/manage/nremt/user/${userID}`).then(pluckData),
)

export let fetchNREMTVerificationStatus = createAsyncThunk(
  'manage/fetchNREMTVerificationStatusStatus',
  ({ linkedServiceID, userID }: { linkedServiceID: string; userID: string }) =>
    axios
      .get<VerificationStatus>(
        `/api/manage/nremt/verification/${linkedServiceID}/${userID}`,
      )
      .then(pluckData),
)

export let fetchLicenseVerificationStatuses = createAsyncThunk(
  'manage/fetchLicenseVerificationStatusesStatus',
  (linkedServiceID: string) =>
    axios
      .get<Record<string, VerificationStatus>>(
        `/api/manage/license/${linkedServiceID}`,
      )
      .then(pluckData),
)

export let fetchCredentialVerificationStatuses = createAsyncThunk(
  'manage/fetchCredentialVerificationStatusesStatus',
  (linkedServiceID: string) =>
    axios
      .get<Record<string, VerificationStatus>>(
        `/api/manage/credential/${linkedServiceID}`,
      )
      .then(pluckData),
)

// Scheduled Assignments Thunks
export let fetchScheduledAssignments = createAsyncThunk(
  'manage/fetchScheduledAssignmentsStatus',
  (linkedServiceID: string) =>
    fetchAllPages<ScheduledAssignment>(
      `/api/manage/${linkedServiceID}/scheduled-assignments`,
      50,
    ),
)

export let fetchScheduledAssignment = createAsyncThunk(
  'manage/fetchScheduledAssignmentStatus',
  (scheduledAssignmentID: string) =>
    axios
      .get<ScheduledAssignment>(
        `/api/manage/scheduled-assignment/${scheduledAssignmentID}`,
      )
      .then(pluckData),
)

export let createScheduledAssignment = createAsyncThunk(
  'manage/createScheduledAssignmentStatus',
  (data: CreateScheduledAssignmentPayload) =>
    axios
      .post<ScheduledAssignment>('/api/manage/scheduled-assignment', data)
      .then(pluckData),
)

export let updateScheduledAssignment = createAsyncThunk(
  'manage/updateScheduledAssignmentStatus',
  (data: UpdateScheduledAssignmentPayload) =>
    axios
      .put<ScheduledAssignment>('/api/manage/scheduled-assignment', data)
      .then(pluckData),
)

export let deleteScheduledAssignment = createAsyncThunk(
  'manage/deleteScheduledAssignmentStatus',
  (scheduledAssignmentID: string) =>
    axios
      .delete<string>(
        `/api/manage/scheduled-assignment/${scheduledAssignmentID}`,
      )
      .then(pluckData),
)

export let fetchNREMTVerificationStatusesByLS = createAsyncThunk(
  'manage/fetchNREMTVerificationStatusesByLS',
  (linkedServiceID: string) =>
    fetchAllPages<VerificationStatusesResponse>(
      `/api/manage/nremt/verification/${linkedServiceID}`,
    ),
)

export let fetchLicenseVerificationStatusesByLS = createAsyncThunk(
  'manage/fetchLicenseVerificationStatusesByLS',
  (linkedServiceID: string) =>
    fetchAllPages<VerificationStatusesResponse>(
      `/api/manage/license/bulk/${linkedServiceID}`,
    ),
)

export let fetchCredentialVerificationStatusesByLS = createAsyncThunk(
  'manage/fetchCredentialVerificationStatusesByLS',
  (linkedServiceID: string) =>
    fetchAllPages<VerificationStatusesResponse>(
      `/api/manage/credential/bulk/${linkedServiceID}`,
    ),
)

type EntityStatus = string | 'unselected'

type ManageState = {
  activeLinkedService: EntityStatus
  userHoursStatus: Status
  userHours: UserHoursV3[]
  groupsStatus: Status
  groups: UserGroup[]
  usersStatus: Status
  users: User[]
  emsIDs: Record<string, EMSID>
  emsIDsStatus: Record<string, Status>
  selectedUserStatus: Status
  selectedUser: SelectedUser | null
  selectedUserCertificatesStatus: Status
  selectedUserCertificates: CertificatesRollupData
  selectedUserLicensesCredentialsStatus: Status
  selectedUserLicensesCredentials: RosterCredentialItem[]
  selectedUserActivityStatus: Status
  selectedUserActivity: RosterUserActivityResponse | null
  selectedUserAssignmentDetailsStatus: Status
  selectedUserAssignmentDetails: SingleUserAssignmentDetails | null
  assignmentsStatus: Status
  assignments: Assignment[]
  completedAssignmentsWithinThresholdStatus: Record<string, Status>
  completedAssignmentsWithinThreshold: Record<string, CompletedCourse[]>
  completedAssignmentsStatus: Status
  completedAssignments: CompletedAssignment[]
  completedCoursesStatus: Status
  completedCourses: CompletedCourse[]
  trainingSeries: TrainingSeries[]
  trainingSeriesStatus: Status
  learningPaths: LearningPath[]
  learningPathsStatus: Status
  foamfratLearningPaths: LearningPath[]
  foamfratLearningPathsStatus: Status
  sections: Section[]
  sectinsStatus: Status
  classes: Class[]
  classesStatus: Status
  courses: Course[]
  coursesStatus: Status
  invites: UserInvite[]
  invitesStatus: Status
  certificates: RecordedCertificate[]
  certificatesStatus: Status
  liveCertificates: LiveCertificate[]
  liveCertificatesStatus: Status
  externalCertificates: ExternalCertificate[]
  externalCertificatesStatus: Status
  credentials: Credential[]
  credentialsStatus: Status
  quizAnalytics: Record<string, QuizData>
  quizAnalyticsStatus: Status
  linkedServicesStatus: Status
  linkedServices: LinkedService[]
  successMessage: string
  errorMessage: string
  licensesStatus: Record<string, Status>
  licenses: Record<string, License[]>
  // V4 assignments properties
  activeAssignmentV4: string | 'unselected'
  assignmentSummariesV4: Record<string, AssignmentSummaryV3[]>
  assignmentSummariesV4Status: Status
  activeAssignmentDetailV4: AssignmentDetailV3 | null
  activeAssignmentDetailV4Status: Status
  assignmentUserProgressV4: UserAssignmentProgress[]
  assignmentUserProgressV4Status: Status
  assignmentCredits: AssignmentCreditsResponse | null
  assignmentCreditsStatus: Status
  lightweightUsersV4: Record<string, LightweightUser[]>
  lightweightUsersV4Status: Record<string, Status>
  elevatedUsers: Record<string, ElevatedUsersResponse>
  elevatedUsersStatus: Record<string, Status>
  courseUserProgressV4: Record<string, CourseUserProgress[]>
  courseUserProgressV4Status: Record<string, Status>
  userDetailsV4: Record<string, User>
  userDetailsV4Status: Record<string, Status>
  nremtLicenses: Record<string, NREMTLicense>
  nremtLicensesStatus: Record<string, Status>
  nremtVerificationStatus: Record<string, VerificationStatus>
  nremtVerificationStatusStatus: Record<string, Status>
  licenseVerificationStatuses: Record<string, VerificationStatus>
  licenseVerificationStatusesStatus: Record<string, Status>
  credentialVerificationStatuses: Record<string, VerificationStatus>
  credentialVerificationStatusesStatus: Record<string, Status>
  // Scheduled Assignments
  scheduledAssignments: Record<string, ScheduledAssignment[]>
  scheduledAssignmentsStatus: Record<string, Status>
  activeScheduledAssignment: ScheduledAssignment | null
  activeScheduledAssignmentStatus: Status
  nremtVerificationsByLS: Record<string, VerificationStatusesResponse[]>
  nremtVerificationsByLSStatus: Record<string, Status>
  licenseVerificationsByLS: Record<string, VerificationStatusesResponse[]>
  licenseVerificationsByLSStatus: Record<string, Status>
  credentialVerificationsByLS: Record<string, VerificationStatusesResponse[]>
  credentialVerificationsByLSStatus: Record<string, Status>
  // Assignment Analytics
  assignmentAnalytics: Record<string, AnalyticsRow[]>
  assignmentAnalyticsStatus: Record<string, Status>
  assignmentAnalyticsMetrics: Record<string, AssignmentAnalyticsMetrics>
  assignmentAnalyticsMetricsStatus: Record<string, Status>
  assignmentAnalyticsFilters: AssignmentAnalyticsFilters
  // Groups V2
  groupsV2: Record<string, GroupsV2Row[]>
  groupsV2Status: Record<string, Status>
  groupsV2Filters: GroupsV2Filters
  // Course Title Screens
  courseTitleScreens: Record<string, TitleScreen>
  courseTitleScreensStatus: Record<string, Status>
  // Course Versions (for info button v1/v2 detection)
  courseVersions: Record<string, CourseVersionResponse>
  courseVersionsStatus: Record<string, Status>
  // Course V2 Content (for info button v2 detail view)
  courseV2Contents: Record<string, CourseV2Content>
  courseV2ContentsStatus: Record<string, Status>
  // Shared assignment date filters (used by both Assignments list and Analytics)
  assignmentDateFilters: {
    startDate: string | null
    endDate: string | null
  }
}

export let initialState: ManageState = {
  activeLinkedService: 'unselected',
  userHoursStatus: 'uninitialized',
  userHours: [],
  groupsStatus: 'uninitialized',
  groups: [],
  usersStatus: 'uninitialized',
  users: [],
  emsIDsStatus: {},
  emsIDs: {},
  selectedUserStatus: 'uninitialized',
  selectedUser: null,
  selectedUserCertificatesStatus: 'uninitialized',
  selectedUserCertificates: [],
  selectedUserLicensesCredentialsStatus: 'uninitialized',
  selectedUserLicensesCredentials: [],
  selectedUserActivityStatus: 'uninitialized',
  selectedUserActivity: null,
  selectedUserAssignmentDetailsStatus: 'uninitialized',
  selectedUserAssignmentDetails: null,
  assignmentsStatus: 'uninitialized',
  assignments: [],
  completedAssignmentsStatus: 'uninitialized',
  completedAssignments: [],
  completedAssignmentsWithinThresholdStatus: {},
  completedAssignmentsWithinThreshold: {},
  completedCoursesStatus: 'uninitialized',
  completedCourses: [],
  trainingSeriesStatus: 'uninitialized',
  trainingSeries: [],
  learningPathsStatus: 'uninitialized',
  learningPaths: [],
  foamfratLearningPathsStatus: 'uninitialized',
  foamfratLearningPaths: [],
  sectinsStatus: 'uninitialized',
  sections: [],
  coursesStatus: 'uninitialized',
  courses: [],
  classesStatus: 'uninitialized',
  classes: [],
  invitesStatus: 'uninitialized',
  invites: [],
  certificatesStatus: 'uninitialized',
  certificates: [],
  liveCertificatesStatus: 'uninitialized',
  liveCertificates: [],
  externalCertificatesStatus: 'uninitialized',
  externalCertificates: [],
  credentialsStatus: 'uninitialized',
  credentials: [],
  quizAnalyticsStatus: 'uninitialized',
  quizAnalytics: {},
  linkedServicesStatus: 'uninitialized',
  linkedServices: [],
  successMessage: '',
  errorMessage: '',
  licensesStatus: {},
  licenses: {},
  // V4 assignments initial state
  activeAssignmentV4: 'unselected',
  assignmentSummariesV4: {},
  assignmentSummariesV4Status: 'uninitialized',
  activeAssignmentDetailV4: null,
  activeAssignmentDetailV4Status: 'uninitialized',
  assignmentUserProgressV4: [],
  assignmentUserProgressV4Status: 'uninitialized',
  assignmentCredits: null,
  assignmentCreditsStatus: 'uninitialized',
  lightweightUsersV4: {},
  lightweightUsersV4Status: {},
  elevatedUsers: {},
  elevatedUsersStatus: {},
  courseUserProgressV4: {},
  courseUserProgressV4Status: {},
  userDetailsV4: {},
  userDetailsV4Status: {},
  nremtLicenses: {},
  nremtLicensesStatus: {},
  nremtVerificationStatus: {},
  nremtVerificationStatusStatus: {},
  licenseVerificationStatuses: {},
  licenseVerificationStatusesStatus: {},
  credentialVerificationStatuses: {},
  credentialVerificationStatusesStatus: {},
  scheduledAssignments: {},
  scheduledAssignmentsStatus: {},
  activeScheduledAssignment: null,
  activeScheduledAssignmentStatus: 'uninitialized',
  nremtVerificationsByLS: {},
  nremtVerificationsByLSStatus: {},
  licenseVerificationsByLS: {},
  licenseVerificationsByLSStatus: {},
  credentialVerificationsByLS: {},
  credentialVerificationsByLSStatus: {},
  assignmentAnalytics: {},
  assignmentAnalyticsStatus: {},
  assignmentAnalyticsMetrics: {},
  assignmentAnalyticsMetricsStatus: {},
  assignmentAnalyticsFilters: {
    groupingView: 'assignment',
    columnVisibility: {},
    statusFilter: 'all',
  },
  groupsV2: {},
  groupsV2Status: {},
  groupsV2Filters: { groupingView: 'group', columnVisibility: {} },
  courseTitleScreens: {},
  courseTitleScreensStatus: {},
  courseVersions: {},
  courseVersionsStatus: {},
  courseV2Contents: {},
  courseV2ContentsStatus: {},
  // Dates are intentionally null here. Computing them in initialState would
  // cache a stale range at module load time. The selector computes defaults
  // from the current date on each call when these are null.
  assignmentDateFilters: {
    startDate: null,
    endDate: null,
  },
}

export let manageSlice = _createSlice({
  name: 'manage',
  initialState,
  reducers: {
    activeLinkedServiceChanged: (state, action: PayloadAction<string>) => {
      state.activeLinkedService = action.payload
    },
    groupsChanged: (state, action: PayloadAction<UserGroup[]>) => {
      state.groups = action.payload
    },
    usersChanged: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload
    },
    assignmentsChanged: (state, action: PayloadAction<Assignment[]>) => {
      state.assignments = action.payload
    },
    trainingSeriesChanged: (state, action: PayloadAction<TrainingSeries[]>) => {
      state.trainingSeries = action.payload
    },
    invitesChanged: (state, action: PayloadAction<UserInvite[]>) => {
      state.invites = action.payload
    },
    certificatesChanged: (
      state,
      action: PayloadAction<RecordedCertificate[]>,
    ) => {
      state.certificates = action.payload
    },
    credentialsChanged: (state, action: PayloadAction<Credential[]>) => {
      state.credentials = action.payload
    },
    successMessageChanged: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload
    },
    errorMessageChanged: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    // V4 Actions
    activeAssignmentV4Changed: (state, action: PayloadAction<string>) => {
      state.activeAssignmentV4 = action.payload
    },
    assignmentAnalyticsFiltersChanged: (
      state,
      action: PayloadAction<Partial<AssignmentAnalyticsFilters>>,
    ) => {
      state.assignmentAnalyticsFilters = {
        ...state.assignmentAnalyticsFilters,
        ...action.payload,
      }
    },
    assignmentDateFiltersChanged: (
      state,
      action: PayloadAction<
        Partial<{ startDate: string | null; endDate: string | null }>
      >,
    ) => {
      state.assignmentDateFilters = {
        ...state.assignmentDateFilters,
        ...action.payload,
      }
    },
    groupsV2FiltersChanged: (
      state,
      action: PayloadAction<Partial<GroupsV2Filters>>,
    ) => {
      state.groupsV2Filters = { ...state.groupsV2Filters, ...action.payload }
    },
    assignmentAnalyticsReset: (state, action: PayloadAction<string>) => {
      state.assignmentAnalyticsStatus[action.payload] = 'uninitialized'
      state.assignmentAnalyticsMetricsStatus[action.payload] = 'uninitialized'
      state.assignmentAnalytics = Object.fromEntries(
        Object.entries(state.assignmentAnalytics).filter(
          ([k]) => k !== action.payload,
        ),
      )
      state.assignmentAnalyticsMetrics = Object.fromEntries(
        Object.entries(state.assignmentAnalyticsMetrics).filter(
          ([k]) => k !== action.payload,
        ),
      )
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserHours.pending, (state) => {
        state.userHoursStatus = 'pending'
      })
      .addCase(fetchUserHours.rejected, (state) => {
        state.userHoursStatus = 'rejected'
      })
      .addCase(
        fetchUserHours.fulfilled,
        (state, action: PayloadAction<UserHoursV3[]>) => {
          state.userHoursStatus = 'fulfilled'
          state.userHours = action.payload
        },
      )
      .addCase(fetchGroups.pending, (state) => {
        state.groupsStatus = 'pending'
      })
      .addCase(fetchGroups.rejected, (state) => {
        state.groupsStatus = 'rejected'
      })
      .addCase(
        fetchGroups.fulfilled,
        (state, action: PayloadAction<UserGroup[]>) => {
          state.groupsStatus = 'fulfilled'
          state.groups = action.payload
        },
      )
      .addCase(fetchUsers.pending, (state) => {
        state.usersStatus = 'pending'
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.usersStatus = 'rejected'
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.usersStatus = 'fulfilled'
        state.users = action.payload
      })
      .addCase(fetchUserEMSID.pending, (state, action) => {
        state.emsIDsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchUserEMSID.rejected, (state, action) => {
        state.emsIDsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchUserEMSID.fulfilled, (state, action) => {
        state.emsIDsStatus[action.meta.arg] = 'fulfilled'
        state.emsIDs[action.meta.arg] = action.payload
      })
      .addCase(fetchSelectedUser.pending, (state) => {
        state.selectedUserStatus = 'pending'
      })
      .addCase(fetchSelectedUser.rejected, (state) => {
        state.selectedUserStatus = 'rejected'
      })
      .addCase(
        fetchSelectedUser.fulfilled,
        (state, action: PayloadAction<SelectedUser>) => {
          state.selectedUserStatus = 'fulfilled'
          state.selectedUser = action.payload
        },
      )
      .addCase(fetchSelectedUserCertificates.pending, (state) => {
        state.selectedUserCertificatesStatus = 'pending'
      })
      .addCase(fetchSelectedUserCertificates.rejected, (state) => {
        state.selectedUserCertificatesStatus = 'rejected'
      })
      .addCase(
        fetchSelectedUserCertificates.fulfilled,
        (state, action: PayloadAction<CertificatesRollupData>) => {
          state.selectedUserCertificatesStatus = 'fulfilled'
          state.selectedUserCertificates = action.payload
        },
      )
      .addCase(fetchSelectedUserLicensesCredentials.pending, (state) => {
        state.selectedUserLicensesCredentialsStatus = 'pending'
      })
      .addCase(fetchSelectedUserLicensesCredentials.rejected, (state) => {
        state.selectedUserLicensesCredentialsStatus = 'rejected'
      })
      .addCase(
        fetchSelectedUserLicensesCredentials.fulfilled,
        (state, action: PayloadAction<RosterCredentialItem[]>) => {
          state.selectedUserLicensesCredentialsStatus = 'fulfilled'
          state.selectedUserLicensesCredentials = action.payload
        },
      )
      .addCase(fetchSelectedUserActivity.pending, (state) => {
        state.selectedUserActivityStatus = 'pending'
      })
      .addCase(fetchSelectedUserActivity.rejected, (state) => {
        state.selectedUserActivityStatus = 'rejected'
      })
      .addCase(
        fetchSelectedUserActivity.fulfilled,
        (state, action: PayloadAction<RosterUserActivityResponse>) => {
          state.selectedUserActivityStatus = 'fulfilled'
          state.selectedUserActivity = action.payload
        },
      )
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsStatus = 'pending'
      })
      .addCase(fetchAssignments.rejected, (state) => {
        state.assignmentsStatus = 'rejected'
      })
      .addCase(
        fetchAssignments.fulfilled,
        (state, action: PayloadAction<Assignment[]>) => {
          state.assignmentsStatus = 'fulfilled'
          state.assignments = action.payload
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.pending,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'pending'
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.rejected,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'rejected'
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.fulfilled,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'fulfilled'
          state.completedAssignmentsWithinThreshold[action.meta.arg] =
            action.payload
        },
      )
      .addCase(fetchCompletedAssignments.pending, (state) => {
        state.completedAssignmentsStatus = 'pending'
      })
      .addCase(fetchCompletedAssignments.rejected, (state) => {
        state.completedAssignmentsStatus = 'rejected'
      })
      .addCase(
        fetchCompletedAssignments.fulfilled,
        (state, action: PayloadAction<CompletedAssignment[]>) => {
          state.completedAssignmentsStatus = 'fulfilled'
          state.completedAssignments = action.payload
        },
      )
      .addCase(fetchCompletedCourses.pending, (state) => {
        state.completedCoursesStatus = 'pending'
      })
      .addCase(fetchCompletedCourses.rejected, (state) => {
        state.completedCoursesStatus = 'rejected'
      })
      .addCase(
        fetchCompletedCourses.fulfilled,
        (state, action: PayloadAction<CompletedCourse[]>) => {
          state.completedCoursesStatus = 'fulfilled'
          state.completedCourses = action.payload
        },
      )
      .addCase(fetchTrainingSeries.pending, (state) => {
        state.trainingSeriesStatus = 'pending'
      })
      .addCase(fetchTrainingSeries.rejected, (state) => {
        state.trainingSeriesStatus = 'rejected'
      })
      .addCase(
        fetchTrainingSeries.fulfilled,
        (state, action: PayloadAction<TrainingSeries[]>) => {
          state.trainingSeriesStatus = 'fulfilled'
          state.trainingSeries = action.payload
        },
      )
      .addCase(fetchLearningPaths.pending, (state) => {
        state.learningPathsStatus = 'pending'
      })
      .addCase(fetchLearningPaths.rejected, (state) => {
        state.learningPathsStatus = 'rejected'
      })
      .addCase(
        fetchLearningPaths.fulfilled,
        (state, action: PayloadAction<LearningPath[]>) => {
          state.learningPathsStatus = 'fulfilled'
          state.learningPaths = action.payload
        },
      )
      .addCase(fetchFoamfratLearningPaths.pending, (state) => {
        state.foamfratLearningPathsStatus = 'pending'
      })
      .addCase(fetchFoamfratLearningPaths.rejected, (state) => {
        state.foamfratLearningPathsStatus = 'rejected'
      })
      .addCase(
        fetchFoamfratLearningPaths.fulfilled,
        (state, action: PayloadAction<LearningPath[]>) => {
          state.foamfratLearningPathsStatus = 'fulfilled'
          state.foamfratLearningPaths = action.payload
        },
      )
      .addCase(fetchSections.pending, (state) => {
        state.sectinsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectinsStatus = 'rejected'
      })
      .addCase(
        fetchSections.fulfilled,
        (state, action: PayloadAction<Section[]>) => {
          state.sectinsStatus = 'fulfilled'
          state.sections = action.payload
        },
      )
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(
        fetchClasses.fulfilled,
        (state, action: PayloadAction<Class[]>) => {
          state.classesStatus = 'fulfilled'
          state.classes = action.payload
        },
      )
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(
        fetchCourses.fulfilled,
        (state, action: PayloadAction<Course[]>) => {
          state.coursesStatus = 'fulfilled'
          state.courses = action.payload
        },
      )
      .addCase(fetchInvites.pending, (state) => {
        state.invitesStatus = 'pending'
      })
      .addCase(fetchInvites.rejected, (state) => {
        state.invitesStatus = 'rejected'
      })
      .addCase(
        fetchInvites.fulfilled,
        (state, action: PayloadAction<UserInvite[]>) => {
          state.invitesStatus = 'fulfilled'
          state.invites = action.payload
        },
      )
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesStatus = 'pending'
      })
      .addCase(fetchCertificates.rejected, (state) => {
        state.certificatesStatus = 'rejected'
      })
      .addCase(
        fetchCertificates.fulfilled,
        (state, action: PayloadAction<RecordedCertificate[]>) => {
          state.certificatesStatus = 'fulfilled'
          state.certificates = action.payload
        },
      )
      .addCase(fetchLiveCertificates.pending, (state) => {
        state.liveCertificatesStatus = 'pending'
      })
      .addCase(fetchLiveCertificates.rejected, (state) => {
        state.liveCertificatesStatus = 'rejected'
      })
      .addCase(
        fetchLiveCertificates.fulfilled,
        (state, action: PayloadAction<LiveCertificate[]>) => {
          state.liveCertificatesStatus = 'fulfilled'
          state.liveCertificates = action.payload
        },
      )
      .addCase(fetchExternalCertificates.pending, (state) => {
        state.externalCertificatesStatus = 'pending'
      })
      .addCase(fetchExternalCertificates.rejected, (state) => {
        state.externalCertificatesStatus = 'rejected'
      })
      .addCase(
        fetchExternalCertificates.fulfilled,
        (state, action: PayloadAction<ExternalCertificate[]>) => {
          state.externalCertificatesStatus = 'fulfilled'
          state.externalCertificates = action.payload
        },
      )
      .addCase(fetchCredentials.pending, (state) => {
        state.credentialsStatus = 'pending'
      })
      .addCase(fetchCredentials.rejected, (state) => {
        state.credentialsStatus = 'rejected'
      })
      .addCase(
        fetchCredentials.fulfilled,
        (state, action: PayloadAction<Credential[]>) => {
          state.credentialsStatus = 'fulfilled'
          state.credentials = action.payload
        },
      )
      .addCase(fetchQuizAnalytics.pending, (state) => {
        state.quizAnalyticsStatus = 'pending'
      })
      .addCase(fetchQuizAnalytics.rejected, (state) => {
        state.quizAnalyticsStatus = 'rejected'
      })
      .addCase(
        fetchQuizAnalytics.fulfilled,
        (state, action: PayloadAction<QuizData>) => {
          state.quizAnalyticsStatus = 'fulfilled'
          state.quizAnalytics[state.activeLinkedService] = action.payload
        },
      )
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchLicenses.pending, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLicenses.rejected, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'fulfilled'
        state.licenses[action.meta.arg] = action.payload
      })
      // V4 Assignments reducers
      .addCase(fetchAssignmentSummariesV4.pending, (state) => {
        state.assignmentSummariesV4Status = 'pending'
      })
      .addCase(fetchAssignmentSummariesV4.rejected, (state) => {
        state.assignmentSummariesV4Status = 'rejected'
      })
      .addCase(fetchAssignmentSummariesV4.fulfilled, (state, action) => {
        state.assignmentSummariesV4Status = 'fulfilled'
        // Group assignments by linkedServiceID
        let grouped = action.payload.reduce(
          (acc, assignment) => {
            let serviceId = assignment.linkedServiceID
            if (!acc[serviceId]) {
              acc[serviceId] = []
            }
            acc[serviceId].push(assignment)
            return acc
          },
          {} as Record<string, AssignmentSummaryV3[]>,
        )
        state.assignmentSummariesV4 = grouped
      })
      .addCase(fetchAssignmentDetailV4.pending, (state) => {
        state.activeAssignmentDetailV4Status = 'pending'
      })
      .addCase(fetchAssignmentDetailV4.rejected, (state) => {
        state.activeAssignmentDetailV4Status = 'rejected'
      })
      .addCase(fetchAssignmentDetailV4.fulfilled, (state, action) => {
        state.activeAssignmentDetailV4Status = 'fulfilled'
        state.activeAssignmentDetailV4 = action.payload
      })
      .addCase(fetchSelectedUserAssignmentDetail.pending, (state) => {
        state.selectedUserAssignmentDetailsStatus = 'pending'
      })
      .addCase(fetchSelectedUserAssignmentDetail.rejected, (state) => {
        state.selectedUserAssignmentDetailsStatus = 'rejected'
      })
      .addCase(fetchSelectedUserAssignmentDetail.fulfilled, (state, action) => {
        state.selectedUserAssignmentDetailsStatus = 'fulfilled'
        state.selectedUserAssignmentDetails = action.payload
      })
      .addCase(fetchAssignmentUserProgressV4.pending, (state) => {
        state.assignmentUserProgressV4Status = 'pending'
      })
      .addCase(fetchAssignmentUserProgressV4.rejected, (state) => {
        state.assignmentUserProgressV4Status = 'rejected'
      })
      .addCase(fetchAssignmentUserProgressV4.fulfilled, (state, action) => {
        state.assignmentUserProgressV4Status = 'fulfilled'
        state.assignmentUserProgressV4 = action.payload.data
      })
      .addCase(fetchLightweightUsersV4.pending, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.lightweightUsersV4Status[linkedServiceID] = 'pending'
      })
      .addCase(fetchLightweightUsersV4.rejected, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.lightweightUsersV4Status[linkedServiceID] = 'rejected'
      })
      .addCase(fetchLightweightUsersV4.fulfilled, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.lightweightUsersV4Status[linkedServiceID] = 'fulfilled'
        state.lightweightUsersV4[linkedServiceID] = action.payload
      })
      .addCase(fetchElevatedUsers.pending, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.elevatedUsersStatus[linkedServiceID] = 'pending'
      })
      .addCase(fetchElevatedUsers.rejected, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.elevatedUsersStatus[linkedServiceID] = 'rejected'
      })
      .addCase(fetchElevatedUsers.fulfilled, (state, action) => {
        let linkedServiceID = action.meta.arg
        state.elevatedUsersStatus[linkedServiceID] = 'fulfilled'
        state.elevatedUsers[linkedServiceID] = action.payload
      })
      .addCase(fetchCourseUserProgressV4.pending, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'pending'
      })
      .addCase(fetchCourseUserProgressV4.rejected, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'rejected'
      })
      .addCase(fetchCourseUserProgressV4.fulfilled, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'fulfilled'
        state.courseUserProgressV4[key] = action.payload.data
      })
      // New ALL pages async thunk cases
      .addCase(fetchAllAssignmentUserProgressV4.pending, (state) => {
        state.assignmentUserProgressV4Status = 'pending'
      })
      .addCase(fetchAllAssignmentUserProgressV4.rejected, (state) => {
        state.assignmentUserProgressV4Status = 'rejected'
      })
      .addCase(fetchAllAssignmentUserProgressV4.fulfilled, (state, action) => {
        state.assignmentUserProgressV4Status = 'fulfilled'
        state.assignmentUserProgressV4 = action.payload
      })
      .addCase(fetchAssignmentCredits.pending, (state) => {
        state.assignmentCreditsStatus = 'pending'
        state.assignmentCredits = null
      })
      .addCase(fetchAssignmentCredits.rejected, (state) => {
        state.assignmentCreditsStatus = 'rejected'
        state.assignmentCredits = null
      })
      .addCase(fetchAssignmentCredits.fulfilled, (state, action) => {
        state.assignmentCreditsStatus = 'fulfilled'
        state.assignmentCredits = action.payload
      })
      .addCase(fetchAllCourseUserProgressV4.pending, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'pending'
      })
      .addCase(fetchAllCourseUserProgressV4.rejected, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'rejected'
      })
      .addCase(fetchAllCourseUserProgressV4.fulfilled, (state, action) => {
        let key = `${action.meta.arg.assignmentId}-${action.meta.arg.courseId}`
        state.courseUserProgressV4Status[key] = 'fulfilled'
        state.courseUserProgressV4[key] = action.payload
      })
      .addCase(fetchUserDetailsV4.pending, (state, action) => {
        action.meta.arg.forEach((userId) => {
          state.userDetailsV4Status[userId] = 'pending'
        })
      })
      .addCase(fetchUserDetailsV4.rejected, (state, action) => {
        action.meta.arg.forEach((userId) => {
          state.userDetailsV4Status[userId] = 'rejected'
        })
      })
      .addCase(fetchUserDetailsV4.fulfilled, (state, action) => {
        Object.keys(action.payload).forEach((userId) => {
          state.userDetailsV4Status[userId] = 'fulfilled'
          state.userDetailsV4[userId] = action.payload[userId]
        })
      })
      .addCase(fetchCourseTitleScreen.pending, (state, action) => {
        state.courseTitleScreensStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseTitleScreen.rejected, (state, action) => {
        state.courseTitleScreensStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseTitleScreen.fulfilled, (state, action) => {
        state.courseTitleScreensStatus[action.meta.arg] = 'fulfilled'
        state.courseTitleScreens[action.meta.arg] = action.payload
      })
      .addCase(fetchManageCourseVersion.pending, (state, action) => {
        state.courseVersionsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchManageCourseVersion.rejected, (state, action) => {
        state.courseVersionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchManageCourseVersion.fulfilled, (state, action) => {
        state.courseVersionsStatus[action.meta.arg] = 'fulfilled'
        state.courseVersions[action.meta.arg] = action.payload
      })
      .addCase(fetchManageCourseV2Content.pending, (state, action) => {
        state.courseV2ContentsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchManageCourseV2Content.rejected, (state, action) => {
        state.courseV2ContentsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchManageCourseV2Content.fulfilled, (state, action) => {
        state.courseV2ContentsStatus[action.meta.arg] = 'fulfilled'
        state.courseV2Contents[action.meta.arg] = action.payload
      })
      .addCase(fetchNREMTLicense.pending, (state, action) => {
        state.nremtLicensesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchNREMTLicense.rejected, (state, action) => {
        state.nremtLicensesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchNREMTLicense.fulfilled, (state, action) => {
        state.nremtLicensesStatus[action.meta.arg] = 'fulfilled'
        state.nremtLicenses[action.meta.arg] = action.payload
      })
      .addCase(fetchNREMTVerificationStatus.pending, (state, action) => {
        state.nremtVerificationStatusStatus[action.meta.arg.userID] = 'pending'
      })
      .addCase(fetchNREMTVerificationStatus.rejected, (state, action) => {
        state.nremtVerificationStatusStatus[action.meta.arg.userID] = 'rejected'
      })
      .addCase(fetchNREMTVerificationStatus.fulfilled, (state, action) => {
        state.nremtVerificationStatusStatus[action.meta.arg.userID] =
          'fulfilled'
        state.nremtVerificationStatus[action.meta.arg.userID] = action.payload
      })
      .addCase(fetchLicenseVerificationStatuses.pending, (state, action) => {
        state.licenseVerificationStatusesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLicenseVerificationStatuses.rejected, (state, action) => {
        state.licenseVerificationStatusesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLicenseVerificationStatuses.fulfilled, (state, action) => {
        state.licenseVerificationStatusesStatus[action.meta.arg] = 'fulfilled'
        state.licenseVerificationStatuses = action.payload
      })
      .addCase(fetchCredentialVerificationStatuses.pending, (state, action) => {
        state.credentialVerificationStatusesStatus[action.meta.arg] = 'pending'
      })
      .addCase(
        fetchCredentialVerificationStatuses.rejected,
        (state, action) => {
          state.credentialVerificationStatusesStatus[action.meta.arg] =
            'rejected'
        },
      )
      .addCase(
        fetchCredentialVerificationStatuses.fulfilled,
        (state, action) => {
          state.credentialVerificationStatusesStatus[action.meta.arg] =
            'fulfilled'
          state.credentialVerificationStatuses = action.payload
        },
      )
      // Scheduled Assignments
      .addCase(fetchScheduledAssignments.pending, (state, action) => {
        state.scheduledAssignmentsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchScheduledAssignments.rejected, (state, action) => {
        state.scheduledAssignmentsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchScheduledAssignments.fulfilled, (state, action) => {
        state.scheduledAssignmentsStatus[action.meta.arg] = 'fulfilled'
        state.scheduledAssignments[action.meta.arg] = action.payload
      })
      .addCase(fetchScheduledAssignment.pending, (state) => {
        state.activeScheduledAssignmentStatus = 'pending'
      })
      .addCase(fetchScheduledAssignment.rejected, (state) => {
        state.activeScheduledAssignmentStatus = 'rejected'
      })
      .addCase(fetchScheduledAssignment.fulfilled, (state, action) => {
        state.activeScheduledAssignmentStatus = 'fulfilled'
        state.activeScheduledAssignment = action.payload
      })
      .addCase(updateScheduledAssignment.fulfilled, (state, action) => {
        if (state.activeScheduledAssignment?.id === action.payload.id) {
          state.activeScheduledAssignment = action.payload
        }
      })
      .addCase(deleteScheduledAssignment.fulfilled, (state, action) => {
        let deletedId = action.meta.arg
        Object.keys(state.scheduledAssignments).forEach((linkedServiceID) => {
          state.scheduledAssignments[linkedServiceID] =
            state.scheduledAssignments[linkedServiceID].filter(
              (sa) => sa.id !== deletedId,
            )
        })
        if (state.activeScheduledAssignment?.id === deletedId) {
          state.activeScheduledAssignment = null
        }
      })
      .addCase(fetchNREMTVerificationStatusesByLS.pending, (state, action) => {
        state.nremtVerificationsByLSStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchNREMTVerificationStatusesByLS.rejected, (state, action) => {
        state.nremtVerificationsByLSStatus[action.meta.arg] = 'rejected'
      })
      .addCase(
        fetchNREMTVerificationStatusesByLS.fulfilled,
        (state, action) => {
          state.nremtVerificationsByLSStatus[action.meta.arg] = 'fulfilled'
          state.nremtVerificationsByLS[action.meta.arg] = action.payload
        },
      )
      .addCase(
        fetchLicenseVerificationStatusesByLS.pending,
        (state, action) => {
          state.licenseVerificationsByLSStatus[action.meta.arg] = 'pending'
        },
      )
      .addCase(
        fetchLicenseVerificationStatusesByLS.rejected,
        (state, action) => {
          state.licenseVerificationsByLSStatus[action.meta.arg] = 'rejected'
        },
      )
      .addCase(
        fetchLicenseVerificationStatusesByLS.fulfilled,
        (state, action) => {
          state.licenseVerificationsByLSStatus[action.meta.arg] = 'fulfilled'
          state.licenseVerificationsByLS[action.meta.arg] = action.payload
        },
      )
      .addCase(
        fetchCredentialVerificationStatusesByLS.pending,
        (state, action) => {
          state.credentialVerificationsByLSStatus[action.meta.arg] = 'pending'
        },
      )
      .addCase(
        fetchCredentialVerificationStatusesByLS.rejected,
        (state, action) => {
          state.credentialVerificationsByLSStatus[action.meta.arg] = 'rejected'
        },
      )
      .addCase(
        fetchCredentialVerificationStatusesByLS.fulfilled,
        (state, action) => {
          state.credentialVerificationsByLSStatus[action.meta.arg] = 'fulfilled'
          state.credentialVerificationsByLS[action.meta.arg] = action.payload
        },
      )
      .addCase(fetchAssignmentAnalytics.pending, (state, action) => {
        state.assignmentAnalyticsStatus[action.meta.arg.linkedServiceID] =
          'pending'
      })
      .addCase(fetchAssignmentAnalytics.rejected, (state, action) => {
        state.assignmentAnalyticsStatus[action.meta.arg.linkedServiceID] =
          'rejected'
      })
      .addCase(fetchAssignmentAnalytics.fulfilled, (state, action) => {
        state.assignmentAnalyticsStatus[action.meta.arg.linkedServiceID] =
          'fulfilled'
        state.assignmentAnalytics[action.meta.arg.linkedServiceID] =
          action.payload
      })
      .addCase(fetchAssignmentAnalyticsMetrics.pending, (state, action) => {
        state.assignmentAnalyticsMetricsStatus[
          action.meta.arg.linkedServiceID
        ] = 'pending'
      })
      .addCase(fetchAssignmentAnalyticsMetrics.rejected, (state, action) => {
        state.assignmentAnalyticsMetricsStatus[
          action.meta.arg.linkedServiceID
        ] = 'rejected'
      })
      .addCase(fetchAssignmentAnalyticsMetrics.fulfilled, (state, action) => {
        state.assignmentAnalyticsMetricsStatus[
          action.meta.arg.linkedServiceID
        ] = 'fulfilled'
        state.assignmentAnalyticsMetrics[action.meta.arg.linkedServiceID] =
          action.payload
      })
      .addCase(fetchGroupsV2.pending, (state, action) => {
        state.groupsV2Status[action.meta.arg] = 'pending'
      })
      .addCase(fetchGroupsV2.rejected, (state, action) => {
        state.groupsV2Status[action.meta.arg] = 'rejected'
      })
      .addCase(fetchGroupsV2.fulfilled, (state, action) => {
        state.groupsV2Status[action.meta.arg] = 'fulfilled'
        state.groupsV2[action.meta.arg] = action.payload
      })
  },
})

export let {
  activeLinkedServiceChanged,
  groupsChanged,
  usersChanged,
  assignmentsChanged,
  trainingSeriesChanged,
  invitesChanged,
  certificatesChanged,
  credentialsChanged,
  successMessageChanged,
  errorMessageChanged,
  activeAssignmentV4Changed,
  assignmentDateFiltersChanged,
  assignmentAnalyticsFiltersChanged,
  assignmentAnalyticsReset,
  groupsV2FiltersChanged,
} = manageSlice.actions

export let selectActiveLinkedService = (state: AppRootState) =>
  state.manage.activeLinkedService

export let selectUserHoursByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.userHours.find((uh) => uh.linkedServiceID === id)
export let selectUserHoursStatus = (state: AppRootState) =>
  state.manage.userHoursStatus
export let selectQuizAnalytics = (state: AppRootState) =>
  state.manage.quizAnalytics

export let selectGroups = (state: AppRootState) =>
  [...state.manage.groups].sort((a, b) => a.name.localeCompare(b.name))
export let selectGroupsStatus = (state: AppRootState) =>
  state.manage.groupsStatus
export let selectGroupById = (id: string) => (state: AppRootState) =>
  state.manage.groups.find((g) => g.id === id)
export let selectGroupsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.groups
      .filter((g) => g.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectUsers = (state: AppRootState) => state.manage.users
export let selectUsersStatus = (state: AppRootState) => state.manage.usersStatus
export let selectUsersSortedWithName = (state: AppRootState) =>
  state.manage.users
    .map((user) => ({
      ...user,
      ...(user.lastName === 'Unknown'
        ? {
            name: user.primaryEmail,
            firstName: user.primaryEmail,
            lastName: user.primaryEmail,
          }
        : {
            name: `${user.firstName} ${user.lastName}`,
          }),
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectUserById = (id: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).find((u) => u.id === id)
export let selectUserByEmail = (email: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).find((u) => u.primaryEmail === email)
export let selectUsersByGroupId = (id: string) => (state: AppRootState) =>
  state.manage.users
    .filter((u) => selectGroupById(id)(state)?.users?.some((gu) => gu === u.id))
    .map((user) => ({
      ...user,
      name: `${user.firstName} ${user.lastName}`,
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectUsersByLinkedService = (id: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).filter((u) =>
    u.linkedServiceIDs?.includes(id),
  )
export let selectAlphabetizedUsersByLinkedService =
  (id: string) => (state: AppRootState) =>
    selectUsersByLinkedService(id)(state).sort((a, b) =>
      a.lastName.localeCompare(b.lastName),
    )
export let selectUsersByNames = (names: string[]) => (state: AppRootState) =>
  state.manage.users.filter((u) =>
    names.includes(`${u.firstName} ${u.lastName}`),
  )

export let selectUserEMSID = (userID: string) => (state: AppRootState) =>
  state.manage.emsIDs[userID]
export let selectUserEMSIDStatus = (userID: string) => (state: AppRootState) =>
  state.manage.emsIDsStatus[userID]

export let selectSelectedUserStatus = (state: AppRootState) =>
  state.manage.selectedUserStatus
export let selectSelectedUser = (state: AppRootState) =>
  state.manage.selectedUser
export let selectSelectedUserCertificatesStatus = (state: AppRootState) =>
  state.manage.selectedUserCertificatesStatus
export let selectSelectedUserCertificates = (state: AppRootState) =>
  state.manage.selectedUserCertificates
export let selectSelectedUserLicensesCredentialsStatus = (
  state: AppRootState,
) => state.manage.selectedUserLicensesCredentialsStatus
export let selectSelectedUserLicensesCredentials = (state: AppRootState) =>
  state.manage.selectedUserLicensesCredentials
export let selectSelectedUserActivityStatus = (state: AppRootState) =>
  state.manage.selectedUserActivityStatus
export let selectSelectedUserActivity = (state: AppRootState) =>
  state.manage.selectedUserActivity

export let selectAssignments = (state: AppRootState) =>
  state.manage.assignments
    .filter((a) => a.name)
    .sort((a, b) => a.name.localeCompare(b.name))
export let selectAssignmentsStatus = (state: AppRootState) =>
  state.manage.assignmentsStatus
export let selectAssignmentById = (id: string) => (state: AppRootState) =>
  state.manage.assignments.find((a) => a.id === id)
export let selectAssignmentsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.assignments
      .filter((a) => a.name && a.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))
export let selectUsersByAssignmentId = (id: string) => (state: AppRootState) =>
  state.manage.users
    .filter((u) =>
      selectAssignmentById(id)(state)?.users.some((au) => au === u.id),
    )
    .map((user) => ({
      ...user,
      name: `${user.firstName} ${user.lastName}`,
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectCompletedAssignmentsWithinThreshold =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignmentsWithinThreshold[id]
export let selectCompletedAssignmentsWithinThresholdStatus =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignmentsWithinThresholdStatus[id]
export let selectCompletedAssignmentsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignments
      .filter((a) => a.linkedServiceID === id)
      .sort((a, b) => dayjs(b.dateCompleted).diff(a.dateCompleted))
export let selectCompletedAssignmentsStatus = (state: AppRootState) =>
  state.manage.completedAssignmentsStatus

export let selectSections = (state: AppRootState) => state.manage.sections
export let selectSectionsStatus = (state: AppRootState) =>
  state.manage.sectinsStatus

export let selectCompletedCourses = (state: AppRootState) =>
  state.manage.completedCourses
export let selectCompletedCoursesByAssignment =
  (assignmentID: string) => (state: AppRootState) =>
    state.manage.completedCourses.filter(
      (cc) => cc.assignmentID === assignmentID,
    )
export let selectCompletedCoursesStatus = (state: AppRootState) =>
  state.manage.completedCoursesStatus

export let selectTrainingSeries = (state: AppRootState) =>
  state.manage.trainingSeries
export let selectTrainingSeriesStatus = (state: AppRootState) =>
  state.manage.trainingSeriesStatus
export let selectTrainingSeriesById =
  (trainingSeriesId?: string) => (state: AppRootState) =>
    state.manage.trainingSeries.find((ts) => ts.id === trainingSeriesId)
export let selectTrainingSeriesByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.trainingSeries
      .filter((ts) => ts.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectLearningPaths = (state: AppRootState) =>
  state.manage.learningPaths
export let selectLearningPathsStatus = (state: AppRootState) =>
  state.manage.learningPathsStatus
export let selectFoamfratLearningPaths = (state: AppRootState) =>
  state.manage.foamfratLearningPaths
export let selectFoamfratLearningPathsStatus = (state: AppRootState) =>
  state.manage.foamfratLearningPathsStatus
export let selectLearningPathsByLinkedService =
  (id: string) => (state: AppRootState) => {
    if (id === 'unselected') return []

    let allPaths = [
      ...state.manage.learningPaths,
      ...state.manage.foamfratLearningPaths.filter(
        (path) => path.category !== 'New York',
      ),
    ]

    let filteredPaths = allPaths.filter(
      (path) => !path.linkedServiceID || path.linkedServiceID === id,
    )

    return sortBy(prop('name'), filteredPaths)
  }

export let selectClasses = (state: AppRootState) => state.manage.classes
export let selectClassesStatus = (state: AppRootState) =>
  state.manage.classesStatus

export let selectCourses = (state: AppRootState) => state.manage.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.manage.coursesStatus
export let selectCoursesByLinkedService =
  (id: string) => (state: AppRootState) => {
    if (id === 'unselected') return []
    return state.manage.courses
      .filter((c) => !c.linkedServiceID || c.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))
  }
export let selectCoursesByAssignmentByID =
  (id: string) => (state: AppRootState) =>
    state.manage.courses
      .filter((c) =>
        selectAssignmentById(id)(state)?.courses.some((ac) => ac === c.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
export let selectCoursesByTrainingSeriesID =
  (id: string) => (state: AppRootState) =>
    state.manage.courses
      .filter((c) =>
        selectTrainingSeriesById(id)(state)?.courseIDs.some(
          (tsc) => tsc === c.id,
        ),
      )
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectInvites = (state: AppRootState) => state.manage.invites
export let selectInvitesStatus = (state: AppRootState) =>
  state.manage.invitesStatus
export let selectInvitesByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.invites
      .filter((a) => a.linkedServiceID === id)
      .sort((a, b) => a.userEmail.localeCompare(b.userEmail))

export let selectCertificates = (state: AppRootState) =>
  state.manage.certificates
export let selectCertificatesStatus = (state: AppRootState) =>
  state.manage.certificatesStatus
export let selectCertificatesByUserID = (id: string) => (state: AppRootState) =>
  state.manage.certificates.filter((c) => c.userID === id)

export let selectLiveCertificatesByUserID =
  (id: string) => (state: AppRootState) =>
    state.manage.liveCertificates.filter((c) => c.userID === id)
export let selectLiveCertificatesStatus = (state: AppRootState) =>
  state.manage.liveCertificatesStatus

export let selectExternalCertificatesByUserID =
  (id: string) => (state: AppRootState) =>
    state.manage.externalCertificates.filter((c) => c.userID === id)
export let selectExternalCertificatesStatus = (state: AppRootState) =>
  state.manage.externalCertificatesStatus

export let selectCredentials = (state: AppRootState) =>
  state.manage.credentials.map((c) => ({
    ...c,
    credentialType: typeof c.type === 'string' ? c.type : c.type.other,
  }))
export let selectCredentialsStatus = (state: AppRootState) =>
  state.manage.credentialsStatus
export let selectCredentialsByUserId = (id: string) => (state: AppRootState) =>
  selectCredentials(state)
    .filter((c) => c.userID === id)
    .sort((a, b) => dayjs(b.createdTime).diff(a.createdTime))

export let selectLinkedServices = (state: AppRootState) =>
  state.manage.linkedServices
export let selectLinkedServiceById = (id: string) => (state: AppRootState) =>
  state.manage.linkedServices.find((x) => x.id === id)
export let selectLinkedServicesByIds =
  (ids: string[]) => (state: AppRootState) =>
    state.manage.linkedServices.filter((x) => ids.includes(x.id ?? ''))
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.manage.linkedServicesStatus

export let selectSuccessMessage = (state: AppRootState) =>
  state.manage.successMessage

export let selectErrorMessage = (state: AppRootState) =>
  state.manage.errorMessage

export let selectLicenses = (state: AppRootState) => state.manage.licenses
export let selectLicensesStatus = (state: AppRootState) =>
  state.manage.licensesStatus

export let selectGroupsWithDefaults =
  (linkedServiceID: string) =>
  (state: AppRootState): UserGroup[] => [
    ...['EMT', 'AEMT', 'Paramedic', 'Registered Nurse'].map((licenseType) => ({
      id: licenseType,
      name: `${licenseType} (auto)`,
      linkedServiceID: 'FOAMfrat',
      users: selectLightweightUsersV4ByService(linkedServiceID)(state)
        .filter((u) =>
          u.licenseTypes?.some(
            (license) => license.toLowerCase() === licenseType.toLowerCase(),
          ),
        )
        .map((u) => u.id),
    })),
    ...selectGroupsByLinkedService(linkedServiceID)(state),
  ]

// V4 Assignments Selectors following Events patterns
export let selectActiveAssignmentV4 = (state: AppRootState) =>
  state.manage.activeAssignmentV4

export let selectAssignmentSummariesV4 = (state: AppRootState) =>
  Object.values(state.manage.assignmentSummariesV4).flat()

export let selectAssignmentSummariesV4Status = (state: AppRootState) =>
  state.manage.assignmentSummariesV4Status

export let selectActiveAssignmentDetailV4 = (state: AppRootState) =>
  state.manage.activeAssignmentDetailV4

export let selectActiveAssignmentDetailV4Status = (state: AppRootState) =>
  state.manage.activeAssignmentDetailV4Status

export let selectSelectedUserAssignmentDetails = (state: AppRootState) =>
  state.manage.selectedUserAssignmentDetails

export let selectSelectedUserAssignmentDetailsStatus = (state: AppRootState) =>
  state.manage.selectedUserAssignmentDetailsStatus

export let selectAssignmentUserProgressV4 = (state: AppRootState) =>
  state.manage.assignmentUserProgressV4

export let selectAssignmentUserProgressV4Status = (state: AppRootState) =>
  state.manage.assignmentUserProgressV4Status

export let selectAssignmentCredits = (state: AppRootState) =>
  state.manage.assignmentCredits

export let selectAssignmentCreditsStatus = (state: AppRootState) =>
  state.manage.assignmentCreditsStatus

export let selectLightweightUsersV4 = (state: AppRootState) =>
  state.manage.lightweightUsersV4

export let selectLightweightUsersV4Status = (state: AppRootState) =>
  state.manage.lightweightUsersV4Status

export let selectLightweightUsersV4ByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.lightweightUsersV4[linkedServiceID] || []

export let selectLightweightUsersV4StatusByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.lightweightUsersV4Status[linkedServiceID] || 'uninitialized'

export let selectLightweightUserByIdV4 =
  (linkedServiceID: string, userId: string) => (state: AppRootState) =>
    state.manage.lightweightUsersV4[linkedServiceID]?.find(
      (u) => u.id === userId,
    )

export let selectElevatedUsers = (state: AppRootState) =>
  state.manage.elevatedUsers

export let selectElevatedUsersStatus = (state: AppRootState) =>
  state.manage.elevatedUsersStatus

export let selectElevatedUsersByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.elevatedUsers[linkedServiceID] || {
      creators: [],
      managers: [],
    }

export let selectElevatedUsersStatusByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.elevatedUsersStatus[linkedServiceID] || 'uninitialized'

export let selectUserDetailsV4 = (state: AppRootState) =>
  state.manage.userDetailsV4

export let selectUserDetailsByIdV4 = (id: string) => (state: AppRootState) =>
  state.manage.userDetailsV4[id]

export let selectUserDetailsV4Status = (state: AppRootState) =>
  state.manage.userDetailsV4Status

export let selectUserDetailsV4StatusById =
  (id: string) => (state: AppRootState) =>
    state.manage.userDetailsV4Status[id] || 'uninitialized'

export let selectCourseUserProgressV4 = (state: AppRootState) =>
  state.manage.courseUserProgressV4

export let selectCourseUserProgressV4ByKey =
  (assignmentId: string, courseId: string) => (state: AppRootState) =>
    state.manage.courseUserProgressV4[`${assignmentId}-${courseId}`] || []

export let selectCourseUserProgressV4Status = (state: AppRootState) =>
  state.manage.courseUserProgressV4Status

export let selectCourseUserProgressV4StatusByKey =
  (assignmentId: string, courseId: string) => (state: AppRootState) =>
    state.manage.courseUserProgressV4Status[`${assignmentId}-${courseId}`] ||
    'uninitialized'

export let selectNREMTLicense = (userID: string) => (state: AppRootState) =>
  state.manage.nremtLicenses[userID]
export let selectNREMTLicenseStatus =
  (userID: string) => (state: AppRootState) =>
    state.manage.nremtLicensesStatus[userID]

export let selectNREMTVerification =
  (userID: string) => (state: AppRootState) =>
    state.manage.nremtVerificationStatus[userID]
export let selectNREMTVerificationStatus =
  (userID: string) => (state: AppRootState) =>
    state.manage.nremtVerificationStatusStatus[userID]

export let selectLicenseVerifications = (state: AppRootState) =>
  state.manage.licenseVerificationStatuses
export let selectLicenseVerificationsStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.licenseVerificationStatusesStatus[linkedServiceID]

export let selectCredentialVerifications = (state: AppRootState) =>
  state.manage.credentialVerificationStatuses
export let selectCredentialVerificationsStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.credentialVerificationStatusesStatus[linkedServiceID]

export let selectNREMTVerificationsByLS =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.nremtVerificationsByLS[linkedServiceID]
export let selectNREMTVerificationsByLSStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.nremtVerificationsByLSStatus[linkedServiceID]

export let selectLicenseVerificationsByLS =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.licenseVerificationsByLS[linkedServiceID]
export let selectLicenseVerificationsByLSStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.licenseVerificationsByLSStatus[linkedServiceID]

export let selectCredentialVerificationsByLS =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.credentialVerificationsByLS[linkedServiceID]
export let selectCredentialVerificationsByLSStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.credentialVerificationsByLSStatus[linkedServiceID]

// Scheduled Assignments Selectors
export let selectScheduledAssignments = (state: AppRootState) =>
  state.manage.scheduledAssignments

export let selectScheduledAssignmentsStatus = (state: AppRootState) =>
  state.manage.scheduledAssignmentsStatus

export let selectScheduledAssignmentsByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.scheduledAssignments[linkedServiceID] || []

export let selectScheduledAssignmentsStatusByService =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.scheduledAssignmentsStatus[linkedServiceID] || 'uninitialized'

// Assignment Analytics Selectors
export let selectAssignmentAnalytics =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.assignmentAnalytics[linkedServiceID] ?? []

export let selectAssignmentAnalyticsStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.assignmentAnalyticsStatus[linkedServiceID] ?? 'uninitialized'

export let selectAssignmentAnalyticsMetrics =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.assignmentAnalyticsMetrics[linkedServiceID] ?? null

export let selectAssignmentAnalyticsMetricsStatus =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.assignmentAnalyticsMetricsStatus[linkedServiceID] ??
    'uninitialized'

export let selectAssignmentDateFilters = (state: AppRootState) => {
  let filters = state.manage.assignmentDateFilters
  return {
    startDate: filters.startDate ?? dayjs().subtract(6, 'month').toISOString(),
    endDate: filters.endDate ?? dayjs().add(6, 'month').toISOString(),
  }
}

export let selectAssignmentAnalyticsFilters = (state: AppRootState) =>
  state.manage.assignmentAnalyticsFilters

export let selectActiveScheduledAssignment = (state: AppRootState) =>
  state.manage.activeScheduledAssignment

export let selectActiveScheduledAssignmentStatus = (state: AppRootState) =>
  state.manage.activeScheduledAssignmentStatus

// Groups V2 Selectors
export let selectGroupsV2 =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.groupsV2[linkedServiceID] ?? []

export let selectGroupsV2Status =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.manage.groupsV2Status[linkedServiceID] ?? 'uninitialized'

export let selectGroupsV2Filters = (state: AppRootState) =>
  state.manage.groupsV2Filters

export let selectCourseTitleScreen =
  (courseID: string) => (state: AppRootState) =>
    state.manage.courseTitleScreens[courseID] ?? null

export let selectCourseTitleScreenStatus =
  (courseID: string) => (state: AppRootState) =>
    state.manage.courseTitleScreensStatus[courseID] ?? 'uninitialized'

export let selectCourseVersion = (courseID: string) => (state: AppRootState) =>
  state.manage.courseVersions[courseID] ?? null

export let selectCourseVersionStatus =
  (courseID: string) => (state: AppRootState) =>
    state.manage.courseVersionsStatus[courseID] ?? 'uninitialized'

export let selectCourseV2Content =
  (courseID: string) => (state: AppRootState) =>
    state.manage.courseV2Contents[courseID] ?? null

export let selectCourseV2ContentStatus =
  (courseID: string) => (state: AppRootState) =>
    state.manage.courseV2ContentsStatus[courseID] ?? 'uninitialized'

export default manageSlice.reducer
